/**
 * Deterministic daily rotation utilities.
 *
 * Same visitors on the same day see the same selection. Once the UK day
 * ticks over, the seed changes and the selection shifts.
 *
 * Uses a tiny mulberry32 PRNG seeded from days-since-epoch + an optional
 * salt so multiple rotating sections on the same page can pick different
 * subsets from the same source list.
 */

function mulberry32(seed) {
  return function () {
    seed |= 0;
    seed = (seed + 0x6D2B79F5) | 0;
    let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function daysSinceEpoch(date = new Date()) {
  // Use UTC midnight so rotation happens once per calendar day regardless
  // of the visitor's timezone.
  const utc = Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
  return Math.floor(utc / (24 * 60 * 60 * 1000));
}

/**
 * Return N items from `list` chosen deterministically for today's date.
 *
 * @param {Array} list   Source list.
 * @param {number} count How many to pick.
 * @param {string} salt  Optional namespace so two sections can pick distinct subsets.
 */
export function pickDaily(list, count, salt = '') {
  if (!Array.isArray(list) || list.length === 0) return [];
  if (list.length <= count) return list.slice(0, count);
  let seed = daysSinceEpoch();
  for (let i = 0; i < salt.length; i++) seed = (seed * 31 + salt.charCodeAt(i)) | 0;
  const rng = mulberry32(seed);
  const arr = list.slice();
  // Fisher–Yates with seeded RNG
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr.slice(0, count);
}
